import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
  super.connectedCallback();

  const { signal } = this.#controller;
  const target = this.closest('.shopify-section, dialog');

  target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

  this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
    signal,
  });

  this.addEventListener('click', this.#handleThumbnailClick, { signal });
}

  #controller = new AbortController();

  disconnectedCallback() {
  super.disconnectedCallback();

  this.#controller.abort();
}

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
 * Handles clicks on product media thumbnails.
 *
 * If the clicked media is associated with a color variant,
 * update only the Color option while preserving other options
 * such as Size and Logo.
 *
 * @param {MouseEvent} event
 */
#handleThumbnailClick = (event) => {
  const thumbnail = event.target.closest('[data-media-id]');

  if (!(thumbnail instanceof HTMLElement)) return;

  const mediaId = thumbnail.dataset.mediaId;

  if (!mediaId) return;

  const mapData = this.dataset.mediaColorMap;

  if (!mapData) return;

  let mediaColorMap;

  try {
    mediaColorMap = JSON.parse(mapData);
  } catch (error) {
    console.error('Unable to parse media color map.', error);
    return;
  }

  const mediaMatch = mediaColorMap.find(
    (item) => String(item.mediaId) === String(mediaId)
  );

  if (!mediaMatch?.color) return;

  const variantPicker = this
    .closest('.shopify-section, dialog')
    ?.querySelector('variant-picker');

  if (!variantPicker) return;

  const colorOption = Array.from(
    variantPicker.querySelectorAll('fieldset')
  ).find((fieldset) => {
    const legend = fieldset.querySelector('legend');

    return legend?.textContent?.trim().toLowerCase().startsWith('color');
  });

  if (!colorOption) return;

  const colorInput = Array.from(
    colorOption.querySelectorAll('input[type="radio"]')
  ).find((input) => input.value === mediaMatch.color);

  if (!(colorInput instanceof HTMLInputElement)) return;

  if (colorInput.checked) return;

  if (colorInput.dataset.optionAvailable === 'false') return;

  variantPicker.variantChanged({
    target: colorInput,
  });
};

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
